/**
 * One geometry for both the single markers and the cluster badges, so the two
 * never drift apart visually. Emitted as a string because the clusterer needs
 * real DOM while the marker renders through React.
 */

/** Teardrop with the tip at (20, 52) — AdvancedMarker anchors content bottom-centre. */
const PIN_PATH = 'M20 0C9 0 0 8.9 0 19.9 0 33.9 20 52 20 52s20-18.1 20-32.1C40 8.9 31 0 20 0z'

/** The stroke straddles the path, so the viewBox is padded to stop it clipping. */
const PIN_VIEWBOX = '-2 -2 44 56'
const PIN_WIDTH = 44
const PIN_HEIGHT = 56

/** Drawn height of a pin, so callers can offset anything anchored above it. */
export const PIN_MARKER_HEIGHT = PIN_HEIGHT

const CIRCLE_VIEWBOX = '-2 -2 46 46'
const CIRCLE_SIZE = 46

/**
 * Colours reach here from widget settings, which are merchant-editable, so they
 * are constrained to characters that cannot terminate the attribute.
 */
const safeColor = (value: string, fallback: string): string => {
  const cleaned = value.trim().replace(/[^#\w(),.%\s-]/g, '')

  return cleaned || fallback
}

export interface MarkerSvgOptions {
  fill: string
  borderColor?: string
  borderWidth?: number
  /** A cluster count, a hollow ring for a location, or nothing for a plain dot */
  glyph: { type: 'count'; value: number } | { type: 'ring' } | { type: 'none' }
  shape?: 'PIN' | 'CIRCLE'
  /** Rendered size in pixels; the artwork scales via the viewBox */
  size?: number
}

const glyphMarkup = (
  glyph: MarkerSvgOptions['glyph'],
  color: string,
  cx: number,
  cy: number
): string => {
  if (glyph.type === 'none') {
    return ''
  }

  return glyph.type === 'count'
    ? `<text x="${cx}" y="${cy}" fill="${color}" font-family="inherit" font-size="16" font-weight="700" text-anchor="middle" dominant-baseline="central">${glyph.value}</text>`
    : `<circle cx="${cx}" cy="${cy}" r="6" fill="none" stroke="${color}" stroke-width="3"/>`
}

export const markerSvg = ({
  fill,
  borderColor = '#FFFFFF',
  borderWidth = 2,
  glyph,
  shape = 'PIN',
  size
}: MarkerSvgOptions): string => {
  const background = safeColor(fill, '#1A202C')
  const border = safeColor(borderColor, '#FFFFFF')
  const stroke = borderWidth > 0 ? ` stroke="${border}" stroke-width="${borderWidth}"` : ''

  if (shape === 'CIRCLE') {
    const drawn = size ?? CIRCLE_SIZE

    return `<svg width="${drawn}" height="${drawn}" viewBox="${CIRCLE_VIEWBOX}" xmlns="http://www.w3.org/2000/svg">
  <circle cx="21" cy="21" r="20" fill="${background}"${stroke}/>
  ${glyphMarkup(glyph, border, 21, 21)}
</svg>`
  }

  return `<svg width="${PIN_WIDTH}" height="${PIN_HEIGHT}" viewBox="${PIN_VIEWBOX}" xmlns="http://www.w3.org/2000/svg">
  <path d="${PIN_PATH}" fill="${background}"${stroke} stroke-linejoin="round"/>
  ${glyphMarkup(glyph, border, 20, 20)}
</svg>`
}
